import { useMemo, useState } from 'react';
import { machineKeySets, matchesMachineFilters, restrictMachineFilters } from '../utils/machineFilters';
import { matchesLocation, matchesRetarget } from '../utils/inventoryView';
import { countAgingBuckets, normalizeAgingSelection, unitAgingBucketId } from '../utils/dealershipSettings';
import { useDealershipSettings } from '../contexts/settingsContext';

export const createInitialFilters = () => ({
    location: 'All',
    category: 'All',
    newUsed: 'All',
    agingBucket: [],
    riskTier: 'All',
    sellSpeed: 'All',
    buyerHeat: 'All',
    salesman: 'All',
    search: '',
    qualifyingQuote: false,
    retargeting: false,
    // Machine panel: canonical make keys, "<make>|<model>" ids, YEAR_MANUF range.
    makes: [],
    models: [],
    yearFrom: null,
    yearTo: null,
    includeNoYear: true,
});

export const matchesSellSpeed = (unit, sellSpeedFilter) => {
    if (sellSpeedFilter === 'All') return true;
    return (unit.PredictedBucket || '').toLowerCase().includes(sellSpeedFilter.toLowerCase());
};

export const matchesBuyerHeat = (unit, heatFilter) => {
    if (heatFilter === 'All') return true;
    const quoteCount = unit.QuoteCount;
    const daysSince = unit.DaysSinceLastQuote;
    const hasQuotes = quoteCount && !Number.isNaN(quoteCount) && quoteCount > 0;
    if (!hasQuotes || daysSince == null) return heatFilter === 'No Interest';
    if (daysSince <= 7) return heatFilter === 'Hot';
    if (daysSince <= 30) return heatFilter === 'Warm';
    if (daysSince <= 90) return heatFilter === 'Cold';
    return heatFilter === 'No Interest';
};

/**
 * The units passing every filter except Make / Model / Year. A pure
 * function, so the committed units ("Include committed units", v2) go
 * through exactly the same filters as the regular inventory.
 *
 * `committed`: the units are committed deals (sold / pre-sold / sale
 * pending, from the committed endpoint). They never enter the KPIs, aging
 * tiles, Needs attention or signals, so while any of those drill-downs is
 * active (`hasInStockOnlyFilter`: aging bucket, risk tier, buyer interest,
 * sell speed, an anomaly signal, Live Quote ≥ AI Price, Retarget) no
 * committed unit matches, whatever fields it carries, and the rows shown
 * equal the KPI's number. Plain attribute filters (branch, category,
 * new / used, salesman, search, make / model / year) still include them.
 */
export const hasOpportunityFilter = (filters, anomalyFilter) => Boolean(
    filters.qualifyingQuote || filters.retargeting || (anomalyFilter && anomalyFilter.size > 0),
);

/** A KPI / Needs attention drill-down is active (counts the regular inventory only). */
export const hasInStockOnlyFilter = (filters, anomalyFilter) => Boolean(
    hasOpportunityFilter(filters, anomalyFilter)
    || (filters.agingBucket && filters.agingBucket.length > 0)
    || (filters.riskTier && filters.riskTier !== 'All')
    || (filters.buyerHeat && filters.buyerHeat !== 'All')
    || (filters.sellSpeed && filters.sellSpeed !== 'All'),
);

export const filterInventoryUnits = (units, filters, anomalyFilter, aging, { committed = false } = {}) => {
    if (committed && hasInStockOnlyFilter(filters, anomalyFilter)) return [];
    let filtered = units;

    // One branch (today) or several (v2 multi-select, ?branch=A,B).
    if (filters.location !== 'All') filtered = filtered.filter((u) => matchesLocation(u, filters.location));
    if (filters.category !== 'All') filtered = filtered.filter((u) => u.CATEGORY === filters.category);
    if (filters.newUsed !== 'All') filtered = filtered.filter((u) => u.NewUsedStatus === filters.newUsed);
    if (filters.agingBucket.length > 0) filtered = filtered.filter((u) => filters.agingBucket.includes(unitAgingBucketId(u, aging)));
    if (filters.riskTier !== 'All') filtered = filtered.filter((u) => u.RiskTier === filters.riskTier);
    if (filters.sellSpeed !== 'All') filtered = filtered.filter((u) => matchesSellSpeed(u, filters.sellSpeed));
    if (filters.buyerHeat !== 'All') filtered = filtered.filter((u) => matchesBuyerHeat(u, filters.buyerHeat));
    if (filters.salesman !== 'All') filtered = filtered.filter((u) => u.Salesman === filters.salesman);

    if (filters.search) {
        const search = filters.search.toLowerCase();
        filtered = filtered.filter((u) => String(u.StockNumber ?? '').toLowerCase().includes(search)
            || (u.MAKE && u.MAKE.toLowerCase().includes(search))
            || (u.MODEL && u.MODEL.toLowerCase().includes(search)));
    }

    if (anomalyFilter.size > 0) filtered = filtered.filter((u) => anomalyFilter.has(u.AnomalyVerdict));
    if (filters.qualifyingQuote) filtered = filtered.filter((u) => u.HasQualifyingQuote);
    if (filters.retargeting) filtered = filtered.filter(matchesRetarget);

    return filtered;
};

/** Every filter, Make / Model / Year included. */
export const filterInventoryUnitsFully = (units, filters, anomalyFilter, aging, options) => (
    filterInventoryUnits(units, filters, anomalyFilter, aging, options).filter((u) => matchesMachineFilters(u, filters))
);

export default function useInventoryFilters(allData, anomalyFilter, initialFilters = null) {
    // rawFilters is what the user (or their saved prefs) chose. `filters` is
    // that choice restricted to the makes and models this inventory has, so a
    // selection saved for another dealership neither empties the table nor
    // shows a stale pill — and returns if that dealership is loaded again.
    // rawFilters is what gets saved, so the restriction alone never rewrites
    // the user's preferences.
    // `initialFilters`: a view being restored (Back from the unit page, D7).
    const [rawFilters, setFilters] = useState(() => initialFilters || createInitialFilters());
    // The dealership's aging buckets: the filter holds bucket ids 1–4; a
    // legacy label (an old saved filter, a restored view) reads as its id.
    const { aging } = useDealershipSettings();
    const machineKeys = useMemo(() => machineKeySets(allData), [allData]);
    const filters = useMemo(() => {
        const restricted = restrictMachineFilters(rawFilters, machineKeys);
        const agingBucket = Array.isArray(rawFilters.agingBucket) || typeof rawFilters.agingBucket === 'string'
            ? normalizeAgingSelection(rawFilters.agingBucket, aging.buckets)
            : [];
        const same = Array.isArray(restricted.agingBucket)
            && restricted.agingBucket.length === agingBucket.length
            && restricted.agingBucket.every((v, i) => v === agingBucket[i]);
        return same ? restricted : { ...restricted, agingBucket };
    }, [rawFilters, machineKeys, aging]);

    // Every filter except Make/Model/Year. The machine panel counts its
    // options from this list, so each count follows the rest of the page.
    const machineBaseData = useMemo(
        () => filterInventoryUnits(allData, filters, anomalyFilter, aging),
        [allData, filters, anomalyFilter, aging],
    );

    const filteredData = useMemo(
        () => machineBaseData.filter((u) => matchesMachineFilters(u, filters)),
        [machineBaseData, filters],
    );

    // Units per aging bucket (ids 1–4), also under today's keys.
    const stats = useMemo(() => {
        const byId = countAgingBuckets(filteredData, aging);
        return {
            total: filteredData.length,
            fresh: byId[1],
            normal: byId[2],
            slow: byId[3],
            critical: byId[4],
            byId,
        };
    }, [filteredData, aging]);

    return {
        filters,
        rawFilters,
        setFilters,
        filteredData,
        machineBaseData,
        stats,
    };
}
